import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Card, CardContent, Button, Stack, TextField, MenuItem, Chip, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { newsApi, reviewApi } from '../services/api';
import { Review } from '../types';

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  prefecture: string;
  created_at: string;
  updated_at: string;
  url: string;  // NotionページのURL
  tags: string[];
}

// 都道府県データ
const prefectures = [
  { code: "01", name: "北海道" },
  { code: "02", name: "青森県" },
  { code: "03", name: "岩手県" },
  { code: "04", name: "宮城県" },
  { code: "05", name: "秋田県" },
  { code: "06", name: "山形県" },
  { code: "07", name: "福島県" },
  { code: "08", name: "茨城県" },
  { code: "09", name: "栃木県" },
  { code: "10", name: "群馬県" },
  { code: "11", name: "埼玉県" },
  { code: "12", name: "千葉県" },
  { code: "13", name: "東京都" },
  { code: "14", name: "神奈川県" },
  { code: "15", name: "新潟県" },
  { code: "16", name: "富山県" },
  { code: "17", name: "石川県" },
  { code: "18", name: "福井県" },
  { code: "19", name: "山梨県" },
  { code: "20", name: "長野県" },
  { code: "21", name: "岐阜県" },
  { code: "22", name: "静岡県" },
  { code: "23", name: "愛知県" },
  { code: "24", name: "三重県" },
  { code: "25", name: "滋賀県" },
  { code: "26", name: "京都府" },
  { code: "27", name: "大阪府" },
  { code: "28", name: "兵庫県" },
  { code: "29", name: "奈良県" },
  { code: "30", name: "和歌山県" },
  { code: "31", name: "鳥取県" },
  { code: "32", name: "島根県" },
  { code: "33", name: "岡山県" },
  { code: "34", name: "広島県" },
  { code: "35", name: "山口県" },
  { code: "36", name: "徳島県" },
  { code: "37", name: "香川県" },
  { code: "38", name: "愛媛県" },
  { code: "39", name: "高知県" },
  { code: "40", name: "福岡県" },
  { code: "41", name: "佐賀県" },
  { code: "42", name: "長崎県" },
  { code: "43", name: "熊本県" },
  { code: "44", name: "大分県" },
  { code: "45", name: "宮崎県" },
  { code: "46", name: "鹿児島県" },
  { code: "47", name: "沖縄県" },
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // データ読み込み関数
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 都道府県コードから都道府県名を取得
      const prefectureName = selectedPrefecture 
        ? prefectures.find(p => p.code === selectedPrefecture)?.name 
        : undefined;

      // ニュース記事を取得
      const newsResponse = await newsApi.getNews({ 
        prefecture: prefectureName || undefined,
        keyword: keyword || undefined 
      });
      setNewsArticles(newsResponse.articles || []);

      // 公開口コミを取得
      const reviewsResponse = await reviewApi.getPublicReviews({ 
        prefecture_code: selectedPrefecture || undefined 
      });
      setReviews(reviewsResponse.reviews || []);
    } catch (error) {
      console.error('データの読み込みに失敗しました:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPrefecture, keyword, prefectures]);

  // Notion記事を同期する関数
  const syncNotionArticles = useCallback(async (shouldReloadData = false) => {
    setSyncing(true);
    try {
      console.log('Notion記事の同期を開始...');
      const syncResult = await newsApi.syncNotionArticles();
      console.log('Notion記事の同期完了:', syncResult);
      
      // 手動同期の場合は記事を再ロード
      if (shouldReloadData) {
        await loadData();
      }
    } catch (error) {
      console.error('Notion記事の同期に失敗しました:', error);
    } finally {
      setSyncing(false);
    }
  }, [loadData]);

  // 初期データを読み込む
  useEffect(() => {
    const initializeData = async () => {
      // まずNotion記事を同期してから、データをロード
      await syncNotionArticles();
      await loadData();
    };
    
    initializeData();
  }, [syncNotionArticles, loadData]);

  // フィルター変更時の処理
  const handleFilterChange = () => {
    loadData();
  };

  // 日付フォーマット関数
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '') {
      return '日付不明';
    }
    try {
      return new Date(dateString).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '日付不明';
    }
  };

  return (
    <Box>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, // モバイルでメインタイトルサイズを調整
          fontWeight: 700, // より太いフォント
          color: '#2E3440', // ダークグレー
          letterSpacing: '-0.025em' // 文字間隔を狭く（モダンなデザイン）
        }}
      >
        あの街くる街
      </Typography>
      <Typography 
        variant="h6" 
        gutterBottom 
        align="center" 
        color="text.secondary" 
        sx={{ 
          mb: 4,
          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, // モバイルでサブタイトルサイズを調整
          fontWeight: 400,
          color: '#6B7280' // グレーテキスト
        }}
      >
        自治体の地域ニュースと住民の生の声
      </Typography>

      {/* フィルター部分 */}
      <Paper 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: 4,
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.6), transparent)',
          }
        }}
      > {/* モバイルでパディングを調整 */}
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' } // モバイルでフィルタータイトルサイズを調整
          }}
        >
          検索・フィルター
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 }, // モバイルでギャップを小さく
          flexWrap: 'wrap', 
          alignItems: 'center' 
        }}>
          <Box sx={{ minWidth: { xs: 150, sm: 200 } }}> {/* モバイルで最小幅を調整 */}
            <TextField
              select
              label="都道府県"
              value={selectedPrefecture}
              onChange={(e) => setSelectedPrefecture(e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' } // モバイルでラベルサイズを調整
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' } // モバイルで入力テキストサイズを調整
                }
              }}
            >
              <MenuItem value="">すべて</MenuItem>
              {prefectures.map((pref) => (
                <MenuItem key={pref.code} value={pref.code}>
                  {pref.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ minWidth: { xs: 150, sm: 200 } }}>
            <TextField
              label="キーワード"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              fullWidth
              size="small"
              placeholder="記事検索..."
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }
              }}
            />
          </Box>
          <Button 
            variant="contained" 
            onClick={handleFilterChange}
            disabled={loading}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(148, 163, 184, 0.5)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {loading ? '読み込み中...' : '検索'}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* 地域ニュース欄 */}
        <Box sx={{ flex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography 
              variant="h4"
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } // モバイルでタイトルサイズを調整
              }}
            >
              📰 地域ニュース
            </Typography>
            {syncing && (
              <Chip 
                label="🔄 記事同期中..." 
                color="primary" 
                size="small"
                sx={{ 
                  animation: 'pulse 2s infinite',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' } // モバイルでチップのフォントサイズを小さく
                }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => syncNotionArticles(true)}
              disabled={syncing}
              sx={{ 
                ml: 'auto',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                padding: { xs: '4px 8px', sm: '6px 12px' },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                color: '#3B82F6',
                '&:hover': {
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderColor: '#3B82F6',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {syncing ? '同期中...' : '🔄 記事更新'}
            </Button>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' } // モバイルでサブタイトルのフォントサイズを小さく
            }}
          >
            AI生成による最新の地域情報
          </Typography>
          
          <Stack spacing={3} sx={{ maxHeight: '1000px', overflowY: 'auto', pr: 1 }}>
            {newsArticles.length > 0 ? (
              newsArticles.map((article) => (
                <Card 
                  key={article.id} 
                  sx={{ 
                    cursor: 'pointer',
                    minHeight: { xs: '150px', sm: '200px' },
                    borderRadius: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    boxShadow: '0 10px 30px 0 rgba(31, 38, 135, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.4), transparent)',
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
                      '&::before': {
                        background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.8), transparent)',
                      }
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                  onClick={() => {
                    if (article.url) {
                      window.open(article.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}> {/* モバイルでパディングを調整 */}
                    <Typography 
                      variant="h5" 
                      component="h3" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        mb: 2,
                        fontSize: { xs: '1rem', sm: '1.25rem' } // モバイルでタイトルサイズを調整
                      }}
                    >
                      {article.title}
                    </Typography>
                    {article.excerpt && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph 
                        sx={{ 
                          mb: 2,
                          lineHeight: 1.5,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' } // モバイルで本文サイズを調整
                        }}
                      >
                        {article.excerpt}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {article.prefecture && (
                        <Chip 
                          label={article.prefecture} 
                          size="small" 
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
                            }
                          }}
                        />
                      )}
                      {article.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#3B82F6',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            '&:hover': {
                              background: 'rgba(59, 130, 246, 0.2)',
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        fontSize: '0.75rem',
                        fontStyle: 'italic'
                      }}
                    >
                      最終更新: {formatDate(article.updated_at || article.created_at)}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    記事が見つかりませんでした
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Box>

        {/* 住民口コミ欄 */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } // モバイルでタイトルサイズを調整
            }}
          >
            💬 住民口コミ
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' } // モバイルでサブタイトルのフォントサイズを小さく
            }}
          >
            実際に住んでいる方の評価
          </Typography>

          <Stack spacing={3} sx={{ maxHeight: '800px', overflowY: 'auto', pr: 1 }}>
            {reviews.length > 0 ? (
              reviews.slice(0, 5).map((review) => {
                // 総合評価を計算
                const overallRating = Math.round(
                  (review.ratings.childcare_education +
                   review.ratings.medical_welfare +
                   review.ratings.transportation +
                   review.ratings.shopping_commercial +
                   review.ratings.living_environment +
                   review.ratings.government_services) / 6
                );
                
                // 最初に見つかったコメントを表示
                const firstComment = review.comments.childcare_education || 
                                   review.comments.medical_welfare || 
                                   review.comments.transportation ||
                                   review.comments.shopping_commercial ||
                                   review.comments.living_environment ||
                                   review.comments.government_services ||
                                   '';
                
                return (
                  <Card 
                    key={review.review_id}
                    sx={{ 
                      cursor: 'pointer',
                      minHeight: { xs: '120px', sm: '150px' },
                      borderRadius: '20px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      boxShadow: '0 10px 30px 0 rgba(31, 38, 135, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.4), transparent)',
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
                        '&::before': {
                          background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.8), transparent)',
                        }
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                    onClick={() => navigate('/reviews')}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}> {/* モバイルでパディングを調整 */}
                      <Typography 
                        variant="subtitle1" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 'bold',
                          mb: 1,
                          fontSize: { xs: '0.9rem', sm: '1rem' } // モバイルでタイトルサイズを調整
                        }}
                      >
                        {review.address}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ mb: 2 }}
                      >
                        総合評価: ⭐ {overallRating}/5
                      </Typography>
                      {isAuthenticated && firstComment && (
                        <Typography 
                          variant="body2" 
                          paragraph
                          sx={{ 
                            lineHeight: 1.5,
                            mb: 2,
                            fontSize: '0.9rem'
                          }}
                        >
                          {firstComment.length > 100 
                            ? `${firstComment.substring(0, 100)}...` 
                            : firstComment}
                        </Typography>
                      )}
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}
                      >
                        {formatDate(review.created_at)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    口コミが見つかりませんでした
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>

          {/* 口コミ投稿ボタン */}
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              fullWidth
              onClick={() => navigate('/create-review')}
              sx={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              口コミを投稿する
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
